// Final unified Smart Framework Generator
import React, { useState, useMemo, useEffect, useRef } from "react";
import { useGetChecklistTemplatesQuery, useGenerateMasterItemsMutation, useGetProgramChecklistsQuery, useLinkTemplateToRunMutation } from "../../utils/api/endpoints/checklists/checklistApi";
import { useGetProgramQuery, useGetRunScopeSnapshotQuery } from "../../utils/api/endpoints/programsApi";
import { useGetProgramRunsQuery } from "../../utils/api/endpoints/programRunsApi";
import { toast } from "react-hot-toast";
import { 
  FiChevronLeft, 
  FiChevronRight, 
  FiX, 
  FiCheck, 
  FiShield, 
  FiZap, 
  FiLayers, 
  FiRefreshCw,
  FiInfo,
  FiActivity,
  FiEdit
} from "react-icons/fi";
import { WIZARD_QUESTIONS, getFrameworkSuggestion, FrameworkID, FrameworkScores } from "../../utils/checklists/framework-logic";

const FRAMEWORK_METADATA: Record<FrameworkID, { 
  icon: string, 
  name: string, 
  desc: string, 
  strengths: string[] 
}> = {
  asvs: {
    icon: "📘",
    name: "OWASP ASVS 5.0",
    desc: "Comprehensive verification standard for in-depth assessments or compliance-driven engagements.",
    strengths: ["Structured L1/L2/L3 verification levels", "Ideal for compliance (ISO, SOC2, PCI)", "Covers 286+ verification requirements"]
  },
  ctb: {
    icon: "🎯",
    name: "CTB Custom Library",
    desc: "All-in-one internal CTB test case pool. Best for operational pentests covering web+API.",
    strengths: ["Broadest coverage, good default", "Optimized hybrid library for faster delivery"]
  },
  wstg: {
    icon: "🔬",
    name: "WSTG v4.2",
    desc: "Manual grey/black-box web testing guide. Deep-dive methodology for experienced testers.",
    strengths: ["Manual grey/black-box web testing", "Deep-dive methodology for experienced testers"]
  },
  top10: {
    icon: "🔟",
    name: "OWASP Top 10 2025",
    desc: "Quick high-impact risk coverage for web applications. Excellent for rapid risk reviews.",
    strengths: ["Fast web risk reviews", "Widely recognized benchmark"]
  },
  api10: {
    icon: "⚡",
    name: "OWASP API Security Top 10 2023",
    desc: "Purpose-built for API and microservice targets with endpoint-centric test cases.",
    strengths: ["API-first / microservices focus", "Endpoint-centric test cases"]
  }
};

interface GenerateChecklistWizardProps {
  programId: number;
  runId?: number;
  runName?: string;
  asset?: any;
  startDate?: string | Date;
  endDate?: string | Date;
  onClose: () => void;
}

export const ChecklistGenerator: React.FC<GenerateChecklistWizardProps> = ({
  programId,
  runId,
  runName: propRunName,
  asset,
  startDate,
  endDate,
  onClose
}) => {
  const [step, setStep] = useState(1);
  const [sourceMode, setSourceMode] = useState<"SYNC" | "SMART">(runId ? "SYNC" : "SMART");
  
  // Wizard States
  const [wizardStep, setWizardStep] = useState(1);
  const [selectedFramework, setSelectedFramework] = useState<FrameworkID | null>(null);
  
  // Sync mode state
  const [selectedBlueprintId, setSelectedBlueprintId] = useState<number | null>(null);
  
  // Context Queries
  const { data: programData } = useGetProgramQuery(Number(programId));
  const { data: runsData } = useGetProgramRunsQuery({ program_id: programId.toString() }, { skip: !runId });
  const { data: snapshotData } = useGetRunScopeSnapshotQuery(Number(runId), { skip: !runId });
  
  // Normal Template Queries
  const { data: templatesData } = useGetChecklistTemplatesQuery({ active_only: true }, { skip: !!runId && sourceMode === "SYNC" });
  const [generateMaster, { isLoading: isGeneratingMaster }] = useGenerateMasterItemsMutation();

  // Run Sync Queries
  const { data: blueprintsData, isLoading: isLoadingBlueprints } = useGetProgramChecklistsQuery(programId, { skip: !runId || sourceMode !== "SYNC" });
  const [linkToRun, { isLoading: isLinkingToRun }] = useLinkTemplateToRunMutation();

  const isSyncMode = !!runId && sourceMode === "SYNC";
  const isGenerating = isGeneratingMaster || isLinkingToRun;

  // Helper to parse asset primary types
  const assetPrimaryTypes: string[] = useMemo(() => {
    if (runId && snapshotData?.snapshot?.targets) {
      const types = snapshotData.snapshot.targets.map((t: any) => t.target_type || t.type);
      return Array.from(new Set(types)).filter(Boolean) as string[];
    }
    if (!asset || !asset.primary_type) return [];
    try {
      if (typeof asset.primary_type === 'string') {
        const parsed = JSON.parse(asset.primary_type);
        return Array.from(new Set(Array.isArray(parsed) ? parsed : [parsed]));
      }
      return Array.from(new Set(Array.isArray(asset.primary_type) ? asset.primary_type : [asset.primary_type]));
    } catch (e) {
      return [];
    }
  }, [asset, runId, snapshotData]);

  const hasWeb = useMemo(() => assetPrimaryTypes.some((t: string) => t?.toLowerCase().includes('web')), [assetPrimaryTypes]);
  const hasApi = useMemo(() => assetPrimaryTypes.some((t: string) => t?.toLowerCase().includes('api')), [assetPrimaryTypes]);

  // --- Wizard Logic ---
  const getAutoAnswers = (asset: any, assetPrimaryTypes: string[], start?: any, end?: any) => {
    const autoAnswers: Record<string, string> = {};
    if (!asset) return autoAnswers;

    // 1. Target
    const hasWeb = assetPrimaryTypes.some((t: string) => t?.toLowerCase().includes('web'));
    const hasApi = assetPrimaryTypes.some((t: string) => t?.toLowerCase().includes('api'));

    if (hasWeb && hasApi) autoAnswers.target = 'both';
    else if (hasApi) autoAnswers.target = 'api';
    else if (hasWeb) autoAnswers.target = 'web';

    // 2. Goal - Always default to pentest as requested
    autoAnswers.goal = 'pentest';
    const complianceFws = asset.compliance_frameworks || [];
    const isCompliance = complianceFws.length > 0 && !complianceFws.includes('Not Applicable');

    // 3. API Tech
    const hasGraphql = (asset.includes || []).some((i: string) => i?.toLowerCase().includes('graphql'));
    if (hasGraphql) autoAnswers.api_tech = 'graphql';
    else if (hasApi && hasWeb) autoAnswers.api_tech = 'rest_ui';
    else if (hasApi) autoAnswers.api_tech = 'rest_only';
    else autoAnswers.api_tech = 'none';

    // 4. Auth
    const authModel = Array.isArray(asset.authorization_model) ? asset.authorization_model : [];
    if (authModel.includes('RBAC') || authModel.includes('ABAC')) autoAnswers.auth = 'mixed';
    else if (asset.authentication_required === 'None' || asset.authentication_required === 'none') autoAnswers.auth = 'none';
    else if (hasApi) autoAnswers.auth = 'jwt';
    else autoAnswers.auth = 'session';

    // 5. Sensitivity
    const classification = (asset.data_classification || "").toLowerCase();
    if (classification.includes('regulated') || classification.includes('highly')) {
      // Map regulated to healthcare or pii based on common frameworks
      if (complianceFws.some((f: string) => f.includes('HIPAA'))) autoAnswers.sensitivity = 'healthcare';
      else if (complianceFws.some((f: string) => f.includes('PCI'))) autoAnswers.sensitivity = 'financial';
      else autoAnswers.sensitivity = 'pii';
    } else if (classification.includes('confidential') || classification.includes('internal')) {
      autoAnswers.sensitivity = 'internal';
    } else {
      autoAnswers.sensitivity = 'low';
    }

    // 6. Timeframe
    const startDate = start ? new Date(start) : null;
    const endDate = end ? new Date(end) : null;
    if (startDate && endDate) {
      const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays >= 5) autoAnswers.timeframe = 'extended';
      else if (diffDays >= 3) autoAnswers.timeframe = 'comprehensive';
      else if (diffDays >= 1) autoAnswers.timeframe = 'standard';
      else autoAnswers.timeframe = 'quick';
    } else {
      autoAnswers.timeframe = asset.criticality === 'Critical' ? 'extended' : (asset.criticality === 'High' ? 'comprehensive' : 'standard');
    }

    // 7. Compliance Question
    if (isCompliance) {
      const isHighReg = complianceFws.some((f: string) => ['PCI-DSS', 'HIPAA', 'Privacy Act / GDPR'].some(k => f.includes(k)));
      autoAnswers.compliance = isHighReg ? 'high_reg' : 'standard_reg';
    } else {
      autoAnswers.compliance = 'none';
    }

    return autoAnswers;
  };

  // Answers are initialized once on mount from asset data
  const [answers, setAnswers] = useState<Record<string, string>>(() => getAutoAnswers(asset, assetPrimaryTypes, startDate, endDate));

  const scores = useMemo(() => {
    // Start with 0 for all frameworks in wizard mode to avoid bias
    const s: FrameworkScores = { ctb: 0, asvs: 0, top10: 0, wstg: 0, api10: 0 };
    
    Object.entries(answers).forEach(([qId, val]) => {
      const question = WIZARD_QUESTIONS.find(q => q.id === qId);
      const option = question?.options.find(o => o.value === val);
      if (option) {
        Object.keys(s).forEach(fw => {
          s[fw as FrameworkID] += option.scores[fw as FrameworkID] || 0;
        });
      }
    });

    return s;
  }, [answers]);

  const suggestion = useMemo(() => getFrameworkSuggestion(scores), [scores]);

  // Sync answers when props change (specifically for timeframe auto-detection)
  useEffect(() => {
    const freshAuto = getAutoAnswers(asset, assetPrimaryTypes, startDate, endDate);
    setAnswers(prev => {
      // Only update fields that are currently 'default' or if we just mounted
      const updated = { ...prev };
      if (freshAuto.target && prev.target !== freshAuto.target) updated.target = freshAuto.target;
      if (freshAuto.timeframe && prev.timeframe !== freshAuto.timeframe) updated.timeframe = freshAuto.timeframe;
      return updated;
    });
  }, [asset, assetPrimaryTypes, startDate, endDate]);

  // Step 9 States (Final Configuration)
  const [genTarget, setGenTarget] = useState<'web' | 'api' | 'both'>('web');
  const [genLevel, setGenLevel] = useState<1 | 2 | 3>(1);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [customName, setCustomName] = useState("");
  const [customDesc, setCustomDesc] = useState("");

  const availableCategories = useMemo(() => {
    const rawTemplates = Array.isArray(templatesData) ? templatesData : (templatesData as any)?.data || [];
    const frameworkTemplates = rawTemplates.filter((t: any) => {
      const name = (t.template_name || "").toLowerCase();
      const frameworkAttr = (t as any).framework || "";
      return selectedFramework === 'asvs' ? (frameworkAttr === "ASVS" || name.includes("asvs"))
           : selectedFramework === 'top10' ? (frameworkAttr === "TOP10" || frameworkAttr === "OWASP_TOP_10" || name.includes("owasp"))
           : selectedFramework === 'api10' ? (frameworkAttr === "API10" || frameworkAttr === "OWASP_API" || (name.includes("api") && name.includes("10")))
           : selectedFramework === 'wstg' ? (frameworkAttr === "WSTG" || frameworkAttr === "OWASP" || name.includes("wstg"))
           : (name.includes("ctb") || frameworkAttr === "CTB" || frameworkAttr === "CUSTOM");
    });

    const cats = new Set<string>();
    frameworkTemplates.forEach((t: any) => {
      const items = Array.isArray(t.items) ? t.items : [];
      items.forEach((item: any) => {
        if (item.category) cats.add(item.category);
      });
    });
    return Array.from(cats).sort();
  }, [templatesData, selectedFramework]);

  const categoryCounts = useMemo(() => {
    if (!selectedFramework) return {};
    
    const rawTemplates = Array.isArray(templatesData) ? templatesData : (templatesData as any)?.data || [];
    const template = rawTemplates
      .filter((t: any) => {
        const name = (t.template_name || "").toLowerCase();
        const frameworkAttr = (t as any).framework || "";
        return selectedFramework === 'asvs' ? (frameworkAttr === "ASVS" || name.includes("asvs"))
             : selectedFramework === 'top10' ? (frameworkAttr === "TOP10" || frameworkAttr === "OWASP_TOP_10" || name.includes("owasp"))
             : selectedFramework === 'api10' ? (frameworkAttr === "API10" || frameworkAttr === "OWASP_API" || (name.includes("api") && name.includes("10")))
             : selectedFramework === 'wstg' ? (frameworkAttr === "WSTG" || frameworkAttr === "OWASP" || name.includes("wstg"))
             : (name.includes("ctb") || frameworkAttr === "CTB" || frameworkAttr === "CUSTOM");
      })
      .sort((a: any, b: any) => (b.version || 0) - (a.version || 0))[0];

    if (!template || !template.items) return {};

    const counts: Record<string, number> = {};
    availableCategories.forEach(cat => { counts[cat] = 0; });

    template.items.forEach((item: any) => {
      // 1. Level Check
      if (selectedFramework === 'asvs') {
        if (genLevel === 1 && !item.l1) return;
        if (genLevel === 2 && !item.l2) return;
        if (genLevel === 3 && !item.l3) return;
      }

      // 2. Target Check
      if (genTarget !== 'both') {
        const isTargetWeb = genTarget === 'web';
        const isTargetApi = genTarget === 'api';
        if (isTargetWeb && !item.is_web) return;
        if (isTargetApi && !item.is_api) return;
      }

      if (item.category) {
        counts[item.category] = (counts[item.category] || 0) + 1;
      }
    });

    return counts;
  }, [selectedFramework, genLevel, genTarget, templatesData, availableCategories]);

  const asvsLevelCounts = useMemo(() => {
    if (selectedFramework !== 'asvs') return { 1: 0, 2: 0, 3: 0 };
    
    const rawTemplates = Array.isArray(templatesData) ? templatesData : (templatesData as any)?.data || [];
    const template = rawTemplates
      .filter((t: any) => {
        const name = (t.template_name || "").toLowerCase();
        const frameworkAttr = (t as any).framework || "";
        return (frameworkAttr === "ASVS" || name.includes("asvs"));
      })
      .sort((a: any, b: any) => (b.version || 0) - (a.version || 0))[0];

    if (!template || !template.items) return { 1: 0, 2: 0, 3: 0 };

    const counts = { 1: 0, 2: 0, 3: 0 };
    template.items.forEach((item: any) => {
      // Respect the target type filters
      if (genTarget !== 'both') {
        const isTargetWeb = genTarget === 'web';
        const isTargetApi = genTarget === 'api';
        if (isTargetWeb && !item.is_web) return;
        if (isTargetApi && !item.is_api) return;
      }
      
      if (item.l1) counts[1]++;
      if (item.l2) counts[2]++;
      if (item.l3) counts[3]++;
    });
    return counts;
  }, [selectedFramework, templatesData, genTarget]);

  const estimatedCount = useMemo(() => {
    return selectedCategories.reduce((acc, cat) => acc + (categoryCounts[cat] || 0), 0);
  }, [selectedCategories, categoryCounts]);

  const suggestedDetails = useMemo(() => {
    if (!selectedFramework) return { name: "", description: "" };
    
    const assetName = asset?.asset_name || 'Asset';
    const fw = selectedFramework === 'ctb' ? 'CTB' : (selectedFramework === 'asvs' ? 'ASVS 5.0' : selectedFramework?.toUpperCase() || '');
    const target = genTarget.toUpperCase();
    const lValue = genLevel;
    
    return {
      name: `${fw} ${lValue > 0 ? `L${lValue} ` : ''}${target} Checklist - ${assetName}`,
      description: `${fw} security checklist for ${assetName}.`
    };
  }, [asset, selectedFramework, genTarget, genLevel]);

  // Initialize Step 9 config when we land on it
  const step9Initialized = useRef(false);
  const step10Initialized = useRef(false);

  useEffect(() => {
    if (wizardStep === 9 && !step9Initialized.current) {
      // 1. Target
      setGenTarget(hasWeb && hasApi ? 'both' : (hasApi ? 'api' : 'web'));
      
      // 2. Level (ASVS mapping from timeframe choice)
      const level = answers.timeframe === 'extended' ? 3 : (answers.timeframe === 'comprehensive' ? 2 : 1);
      setGenLevel(level as 1 | 2 | 3);

      // 3. Dynamic Categories - Initialize all as selected
      setSelectedCategories(availableCategories);
      
      step9Initialized.current = true;
    } else if (wizardStep !== 9 && wizardStep !== 10) {
      step9Initialized.current = false;
      step10Initialized.current = false;
    }

    if (wizardStep === 10 && !step10Initialized.current) {
        setCustomName(suggestedDetails.name);
        setCustomDesc(suggestedDetails.description);
        step10Initialized.current = true;
    }
  }, [wizardStep, hasWeb, hasApi, answers, availableCategories, categoryCounts, suggestedDetails, customName]);

  useEffect(() => {
    if (wizardStep === 8 && !selectedFramework) setSelectedFramework(suggestion.winner);
  }, [wizardStep, suggestion.winner, selectedFramework]);

  const handleGenerate = async () => {
    try {
      if (isSyncMode) {
        if (!selectedBlueprintId) return;
        await linkToRun({
          runId: Number(runId),
          programChecklistId: selectedBlueprintId
        }).unwrap();
        toast.success("Checklist synchronized from master!");
      } else {
        if (!selectedFramework) return;
        const rawTemplates = Array.isArray(templatesData) ? templatesData : (templatesData as any)?.data || [];
        const frameworkTemplates = rawTemplates
          .filter((t: any) => {
            const name = (t.template_name || "").toLowerCase();
            const frameworkAttr = (t as any).framework || "";
            return selectedFramework === 'asvs' ? (frameworkAttr === "ASVS" || name.includes("asvs"))
                 : selectedFramework === 'top10' ? (frameworkAttr === "TOP10" || frameworkAttr === "OWASP_TOP_10" || name.includes("owasp"))
                 : selectedFramework === 'api10' ? (frameworkAttr === "API10" || frameworkAttr === "OWASP_API" || (name.includes("api") && name.includes("10")))
                 : selectedFramework === 'wstg' ? (frameworkAttr === "WSTG" || frameworkAttr === "OWASP" || name.includes("wstg"))
                 : (name.includes("ctb") || frameworkAttr === "CTB" || frameworkAttr === "CUSTOM");
          })
          .sort((a: any, b: any) => (b.version || 0) - (a.version || 0)) // Higher version first
          .slice(0, 1) || []; // Only take the primary one to avoid merging standard versions

        const tIds = frameworkTemplates.map((t: any) => t.template_id);
        
        await generateMaster({
          programId,
          runId: runId ? Number(runId) : undefined,
          templateId: tIds,
          asvsLevel: genLevel,
          targetType: genTarget === 'both' ? 'Web+API' : (genTarget === 'api' ? 'API' : 'Web'),
          categories: selectedCategories,
          ctx: {}, // Using dynamic categories now
          checklistName: customName || `Dynamic ${selectedFramework.toUpperCase()} Checklist`,
          checklistDescription: customDesc,
          framework: selectedFramework.toUpperCase()
        }).unwrap();

        toast.success("Dynamic checklist generated successfully!");
      }
      onClose();
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to process request");
    }
  };

  const handleWizardNext = () => {
    const next = wizardStep + 1;
    setWizardStep(next === 2 ? 3 : Math.min(next, 10));
  };
  const handleWizardBack = () => {
    const prev = wizardStep - 1;
    setWizardStep(prev === 2 ? 1 : Math.max(prev, 1));
  };

  const runName = useMemo(() => {
    if (propRunName) return propRunName;
    if (!runId) return null;
    
    // 1. Check snapshot first (most specific for the current view)
    const snapName = (snapshotData as any)?.run_name || snapshotData?.snapshot?.run_name;
    if (snapName) return snapName;
    
    // 2. Check program runs list
    const rawRuns = Array.isArray(runsData) ? runsData : (runsData as any)?.data || [];
    const run = rawRuns.find((r: any) => (r.run_id === Number(runId)) || (r.id === Number(runId)));
    if (run) return run.run_name || run.name;

    // 3. Last fallback
    return `Run #${runId}`;
  }, [runId, runsData, snapshotData]);

  const currentQuestion = WIZARD_QUESTIONS[wizardStep - 1];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-[#0A1A44]/80 backdrop-blur-xl" onClick={onClose} />
      
      <div className={`relative w-full ${sourceMode === 'SMART' ? 'max-w-6xl h-[650px]' : 'max-w-xl h-[500px]' } bg-white rounded-[32px] shadow-2xl overflow-hidden flex flex-col md:flex-row border border-white/20 animate-in zoom-in-95 duration-300 transition-all`}>
        
        {sourceMode === 'SMART' && (
          <div className="w-full md:w-64 bg-slate-50 border-r border-slate-100 p-5 flex flex-col">
            <div className="mb-6">
              <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-4">Framework Match</h3>
              <div className="space-y-5">
                {suggestion.ranked.map((fw) => (
                  <div key={fw.id} className="group relative">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[11px] font-bold text-slate-900 uppercase tracking-tight">{fw.id}</span>
                      <span className="text-[10px] font-semibold text-slate-500">{fw.confidence}%</span>
                    </div>
                    <div className="h-2 w-full bg-slate-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-1000 ease-out ${
                          fw.level === 'High' ? 'bg-[#58CC02]' : fw.level === 'Medium' ? 'bg-blue-500' : 'bg-slate-400'
                        }`}
                        style={{ width: `${fw.confidence}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-auto">
              <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <FiZap className="text-[#58CC02] w-3.5 h-3.5 fill-[#58CC02]/20" />
                  <span className="text-[10px] font-bold text-slate-900 uppercase">Top Match</span>
                </div>
                <h4 className="text-lg font-black text-slate-900 uppercase tracking-tight leading-none mb-1.5">
                  {suggestion.winner === 'ctb' ? 'CTB Custom' : suggestion.winner.toUpperCase()}
                </h4>
                <p className="text-[11px] text-slate-500 font-medium leading-tight">
                  Recommended based on {asset?.asset_name || 'asset'} metadata.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="flex-1 flex flex-col relative overflow-hidden bg-white">
          <div className="px-8 pt-6 pb-4 flex items-center justify-between">
            <div>
              <span className="text-[10px] font-bold text-blue-600 uppercase tracking-wider mb-1 block">
                {sourceMode === 'SYNC' ? 'Synchronization' : (wizardStep === 8 ? 'Select Framework' : wizardStep === 9 ? 'Finalize Scope' : wizardStep === 10 ? 'Final Details' : `Step ${wizardStep > 2 ? wizardStep - 1 : wizardStep} of 6`)}
              </span>
              <h2 className="text-xl font-black text-slate-900 tracking-tight">
                {sourceMode === 'SYNC' ? 'Sync from Master' : 'Smart Framework Generator'}
              </h2>
              {runId && (
                <div className="mt-1 flex items-center gap-2 text-[10px] font-bold text-slate-400 uppercase">
                  <span>{programData?.title || `PROG#${programId}`}</span>
                  <FiChevronRight />
                  <span className="text-slate-600">{runName}</span>
                </div>
              )}
            </div>
            <button onClick={onClose} className="p-3 hover:bg-slate-100 rounded-full transition-colors">
              <FiX className="w-6 h-6 text-slate-400" />
            </button>
          </div>

          <div className="flex-1 px-8 py-2 overflow-y-auto custom-scrollbar">
            {runId && step === 1 && (
              <div className="space-y-4">
                <p className="text-base font-bold text-slate-600">Choose your approach for this run.</p>
                <div className="grid grid-cols-1 gap-3">
                  <button
                    onClick={() => { setSourceMode("SMART"); setStep(2); }}
                    className="flex items-center gap-4 p-4 rounded-2xl border-2 border-[#58CC02] bg-green-50/20 hover:bg-green-50 transition-all text-left"
                  >
                    <div className="p-3 rounded-xl bg-[#58CC02] text-white">
                      <FiZap size={20} />
                    </div>
                    <div>
                      <span className="block text-base font-bold text-slate-900">Smart Generator</span>
                      <p className="text-xs text-slate-500 font-medium">Auto-pilot generation with 7-question intelligence.</p>
                    </div>
                  </button>

                  <button
                    onClick={() => { setSourceMode("SYNC"); setStep(2); }}
                    className="flex items-center gap-4 p-4 rounded-2xl border-2 border-slate-100 bg-white hover:border-[#58CC02] transition-all text-left"
                  >
                    <div className="p-3 rounded-xl bg-slate-100 text-slate-400">
                      <FiRefreshCw size={20} />
                    </div>
                    <div>
                      <span className="block text-base font-bold text-slate-900">Sync from Master</span>
                      <p className="text-xs text-slate-500 font-medium">Use an existing checklist blueprint for this program.</p>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {sourceMode === "SYNC" && step === 2 && (
              <div className="space-y-4">
                <p className="text-base font-bold text-slate-600 mb-4 px-1">Select a master blueprint to snapshot.</p>
                {isLoadingBlueprints ? (
                  <div className="py-20 flex justify-center"><FiRefreshCw className="animate-spin text-slate-300" size={32} /></div>
                ) : (
                  <div className="grid grid-cols-1 gap-4 pb-4">
                    {(blueprintsData?.data || []).map((bp: any) => {
                      // Advanced Logic: Use framework from bp or template, unless it is "CUSTOM".
                      // If still missing/custom, infer from the name keywords.
                      let framework = bp.framework && bp.framework !== "CUSTOM" ? bp.framework : (bp.template?.framework && bp.template.framework !== "CUSTOM" ? bp.template.framework : "");
                      
                      const nameLower = (bp.checklist_name || "").toLowerCase();
                      if (!framework || framework === "CUSTOM") {
                        if (nameLower.includes('asvs')) framework = 'ASVS';
                        else if (nameLower.includes('top10') || nameLower.includes('owasp')) framework = 'OWASP_TOP_10';
                        else if (nameLower.includes('api')) framework = 'API10';
                        else if (nameLower.includes('wstg')) framework = 'WSTG';
                      }

                      const fwLower = framework.toLowerCase();
                      const icon = fwLower.includes('asvs') ? "📘" : fwLower.includes('top10') ? "🔟" : fwLower.includes('api') ? "⚡" : fwLower.includes('wstg') ? "🔬" : "🎯";
                      const isSelected = selectedBlueprintId === bp.program_checklist_id;
                      
                      return (
                        <button
                          key={bp.program_checklist_id}
                          onClick={() => setSelectedBlueprintId(bp.program_checklist_id)}
                          className={`group relative p-5 rounded-2xl border-2 transition-all text-left ${
                            isSelected 
                              ? 'border-[#58CC02] bg-green-50/50 shadow-md ring-4 ring-[#58CC02]/5' 
                              : 'border-slate-100 bg-white hover:border-slate-200 hover:bg-slate-50/30'
                          }`}
                        >
                          <div className="flex items-start gap-4">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl transition-all ${isSelected ? 'bg-white shadow-sm' : 'bg-slate-50'}`}>
                              {icon}
                            </div>
                            <div className="flex-1 min-w-0">
                               <div className="flex items-center gap-2 mb-1">
                                  <span className="block font-black text-sm text-slate-900 uppercase tracking-tight truncate">
                                    {bp.checklist_name}
                                  </span>
                                  {isSelected && (
                                    <div className="bg-[#58CC02] text-white p-0.5 rounded-full">
                                      <FiCheck size={10} strokeWidth={4} />
                                    </div>
                                  )}
                               </div>
                               <div className="flex items-center gap-3">
                                  <span className="text-[10px] font-black text-blue-600 bg-blue-50 px-2 py-0.5 rounded uppercase tracking-wider border border-blue-100/50">
                                    {framework || 'CUSTOM'}
                                  </span>
                                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                                    <div className="w-1 h-1 rounded-full bg-slate-300" />
                                    {bp.items?.length || 0} Requirements
                                  </span>
                               </div>
                               {bp.description && (
                                 <p className="mt-2 text-[11px] text-slate-500 font-medium leading-relaxed line-clamp-1">
                                    {bp.description}
                                 </p>
                               )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {sourceMode === "SMART" && (runId ? step >= 2 : true) && (
              wizardStep < 8 ? (
                <div className="space-y-6">
                  {asset && (
                    <div className="bg-blue-50/50 border border-blue-100 rounded-2xl p-4 flex items-start gap-4 animate-in fade-in slide-in-from-top-2 duration-500">
                      <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center text-blue-600 shrink-0">
                        <FiZap size={20} className="fill-blue-600/20" />
                      </div>
                      <div>
                        <p className="text-[10px] font-black text-blue-900 uppercase tracking-[0.15em] mb-1">Contextual Intelligence Active</p>
                        <p className="text-[11px] text-blue-700/80 font-medium leading-relaxed">
                          The following options have been pre-selected based on <span className="font-bold text-blue-900">{asset.asset_name}</span> metadata. 
                          You may review and manually override any selection as required for this specific assessment.
                        </p>
                      </div>
                    </div>
                  )}

                  <p className="text-lg font-black text-slate-900 leading-tight tracking-tight">
                    Question {wizardStep > 2 ? wizardStep - 1 : wizardStep} — {currentQuestion.text.split(' — ')[1]}
                  </p>
                  <div className="grid grid-cols-1 gap-2.5">
                    {currentQuestion.options.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => {
                          setAnswers(prev => ({ ...prev, [currentQuestion.id]: opt.value }));
                          setTimeout(handleWizardNext, 150);
                        }}
                        className={`group flex items-center justify-between p-4 rounded-2xl border-2 transition-all ${
                          answers[currentQuestion.id] === opt.value ? 'border-[#58CC02] bg-green-50 shadow-sm' : 'border-slate-100 bg-white hover:border-slate-200'
                        }`}
                      >
                        <span className={`font-bold text-[13px] uppercase tracking-tight ${answers[currentQuestion.id] === opt.value ? 'text-green-700' : 'text-slate-900'}`}>
                          {opt.label}
                        </span>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${answers[currentQuestion.id] === opt.value ? 'bg-[#58CC02] border-[#58CC02]' : 'border-slate-200'}`}>
                          {answers[currentQuestion.id] === opt.value && <FiCheck className="text-white w-3 h-3" />}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ) : wizardStep === 8 ? (
                <div className="space-y-6">
                  <p className="text-base font-bold text-slate-600">Select your base framework.</p>
                  <div className="grid grid-cols-1 gap-3">
                    {suggestion.ranked.map((fw, index) => {
                      const meta = FRAMEWORK_METADATA[fw.id];
                      return (
                        <button
                          key={fw.id}
                          onClick={() => setSelectedFramework(fw.id)}
                          className={`group p-5 rounded-2xl border-2 transition-all relative text-left ${
                            selectedFramework === fw.id ? 'border-[#58CC02] bg-green-50 shadow-md' : 'border-slate-100 bg-white hover:border-slate-200'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-3">
                              <span className="text-2xl">{meta.icon}</span>
                              <div>
                                <span className="block font-black text-[13px] text-slate-900 uppercase tracking-tight leading-none mb-1">
                                  {meta.name}
                                </span>
                                <div className="flex items-center gap-2">
                                  <span className={`text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest border ${
                                    index === 0 ? 'bg-green-100 border-green-200 text-green-700' : 'bg-blue-50 border-blue-100 text-blue-600'
                                  }`}>
                                    {index === 0 ? "Recommended" : "Alternative"}
                                  </span>
                                  <span className="text-[10px] font-bold text-slate-400">
                                    Match score: {fw.confidence}%
                                  </span>
                                </div>
                              </div>
                            </div>
                            {index === 0 && (
                              <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-[#58CC02] text-white text-[9px] font-black uppercase tracking-widest shadow-sm">
                                <FiShield className="w-3 h-3" />
                                High Confidence
                              </div>
                            )}
                          </div>
                          
                          <p className="text-[11px] text-slate-500 font-medium leading-relaxed mb-3">
                            {meta.desc}
                          </p>

                          <div className="flex flex-wrap gap-2">
                            {meta.strengths.map((str, i) => (
                              <div key={i} className="flex items-center gap-1.5 text-[10px] text-slate-400 font-medium">
                                <div className="w-1 h-1 rounded-full bg-slate-300" />
                                {str}
                              </div>
                            ))}
                          </div>

                          {selectedFramework === fw.id && (
                            <div className="absolute top-1/2 -translate-y-1/2 right-5">
                              <div className="w-6 h-6 rounded-full bg-[#58CC02] text-white flex items-center justify-center shadow-lg">
                                <FiCheck size={14} />
                              </div>
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : wizardStep === 9 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pb-10">
                  <div className="md:col-span-2">
                    <div className="bg-[#0A1A44] rounded-2xl p-4 flex items-center justify-between overflow-hidden relative">
                      <div className="absolute top-0 right-0 w-32 h-32 bg-[#58CC02]/10 rounded-full -mr-16 -mt-16 blur-2xl" />
                      <div className="flex items-center gap-4 relative z-10">
                        <div className="w-12 h-12 rounded-xl bg-[#58CC02]/20 flex items-center justify-center text-[#58CC02]">
                          <FiActivity size={24} className="animate-pulse" />
                        </div>
                        <div>
                          <p className="text-[10px] font-black text-[#58CC02] uppercase tracking-[0.2em] leading-none mb-1">Live Generation Feed</p>
                          <p className="text-white text-lg font-black tracking-tight">
                            ~{estimatedCount} <span className="text-slate-400 font-bold text-sm">Security Verification Items</span>
                          </p>
                        </div>
                      </div>
                      <div className="text-right relative z-10 hidden sm:block">
                        <span className="inline-block px-3 py-1 rounded-full bg-white/5 border border-white/10 text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                          Density: {estimatedCount > 150 ? 'High' : estimatedCount > 50 ? 'Medium' : 'Light'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 block">Target Type Match</label>
                      <div className="flex bg-slate-100 p-1 rounded-xl gap-1">
                        {['web', 'api', 'both'].map((t) => (
                          <button
                            key={t}
                            onClick={() => setGenTarget(t as any)}
                            className={`flex-1 py-2 text-[10px] font-black uppercase rounded-lg transition-all ${
                              genTarget === t ? 'bg-[#0A1A44] text-[#58CC02] shadow-sm' : 'text-slate-400 hover:text-slate-600'
                            }`}
                          >
                            {t === 'both' ? 'Web + API' : t.toUpperCase()}
                          </button>
                        ))}
                      </div>
                      <div className="mt-3 bg-blue-50 border border-blue-100 rounded-xl p-3 flex items-start gap-2.5">
                        <FiInfo className="text-blue-500 mt-0.5" size={14} />
                        <div>
                          <p className="text-[10px] text-blue-700 font-bold uppercase leading-none mb-1">Detected Target</p>
                          <p className="text-[10px] text-blue-600/70 font-medium leading-tight italic">
                            {assetPrimaryTypes.length > 0 ? assetPrimaryTypes.join(", ") : "Manual assessment requested"}
                          </p>
                        </div>
                      </div>
                    </div>

                    {selectedFramework === 'asvs' && (
                      <div>
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 block">ASVS Assurance Level</label>
                        <div className="grid grid-cols-3 gap-2">
                          {[1, 2, 3].map((l) => (
                            <button
                              key={l}
                              onClick={() => setGenLevel(l as any)}
                              className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-1 ${
                                genLevel === l ? 'border-[#58CC02] bg-green-50 shadow-sm' : 'border-slate-100 bg-white hover:border-slate-200'
                              }`}
                            >
                              <span className={`text-[13px] font-black ${genLevel === l ? 'text-green-700' : 'text-slate-900'}`}>L{l}</span>
                              <span className="text-[8px] font-bold text-slate-400 uppercase tracking-tighter">
                                {l === 1 ? 'Foundational' : l === 2 ? 'Standard' : 'Specialist'}
                              </span>
                            </button>
                          ))}
                        </div>
                        <p className="mt-2 text-[9px] text-slate-400 font-medium leading-tight">
                          Level {genLevel} includes {asvsLevelCounts[genLevel]} manual verification requirements for your current selection.
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="bg-slate-50/50 rounded-3xl p-6 border border-slate-100">
                    <div className="flex items-center justify-between mb-4">
                      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Standard Chapters</label>
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => setSelectedCategories(availableCategories)}
                          className="text-[8px] font-bold text-[#58CC02] uppercase hover:underline"
                        >All</button>
                        <button 
                          onClick={() => setSelectedCategories([])}
                          className="text-[8px] font-bold text-slate-400 uppercase hover:underline"
                        >None</button>
                        <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-[#0A1A44] text-[#58CC02] tracking-widest uppercase ml-1">
                          {selectedCategories.filter(cat => (categoryCounts[cat] || 0) > 0).length} Chapters | {estimatedCount} Items
                        </span>
                      </div>
                    </div>
                    
                    <div className="space-y-2 max-h-[350px] overflow-y-auto pr-2 custom-scrollbar">
                      {availableCategories.map((cat) => {
                        const isSelected = selectedCategories.includes(cat);
                        const count = categoryCounts[cat] || 0;
                        const isEmpty = count === 0;
                        const isActive = isSelected && !isEmpty;

                        return (
                          <button
                            key={cat}
                            onClick={() => {
                              if (isEmpty) {
                                toast.error(`"${cat}" has no test cases for current selections (L${genLevel}, ${genTarget.toUpperCase()})`, {
                                  icon: '🚫',
                                  duration: 3000
                                });
                                return;
                              }
                              if (isSelected) setSelectedCategories(prev => prev.filter(c => c !== cat));
                              else setSelectedCategories(prev => [...prev, cat]);
                            }}
                            className={`w-full group flex items-center justify-between p-2.5 rounded-xl border transition-all ${
                              isEmpty ? 'bg-slate-50 border-slate-100 opacity-60 cursor-not-allowed' :
                              isActive ? 'bg-white border-[#58CC02] shadow-sm' : 'bg-slate-100/50 border-transparent text-slate-400 hover:border-slate-200'
                            }`}
                          >
                            <div className="flex items-center gap-2.5">
                                <div className={`p-1.5 rounded-lg transition-colors ${isEmpty ? 'bg-slate-200 text-slate-400' : isActive ? 'bg-green-100 text-green-600' : 'bg-slate-200/50 text-slate-300'}`}>
                                <FiLayers size={12}/>
                              </div>
                              <span className={`text-[10px] font-black uppercase tracking-tight text-left ${isEmpty ? 'text-slate-400' : isActive ? 'text-slate-900' : ''}`}>
                                {cat}
                                {isEmpty && <span className="ml-2 text-[8px] font-bold text-slate-400 lowercase tracking-normal bg-slate-100 px-1.5 py-0.5 rounded">(0 items)</span>}
                              </span>
                            </div>
                            <div className={`shrink-0 w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center transition-all ${isEmpty ? 'border-slate-200 bg-slate-50' : isActive ? 'bg-[#58CC02] border-[#58CC02]' : 'border-slate-300'}`}>
                              {isActive && <FiCheck className="text-white w-2 h-2" />}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : wizardStep === 10 ? (
                <div className="space-y-4 max-w-lg mx-auto py-2">
                  <div className="text-center space-y-1">
                    <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-blue-50 text-blue-600 mb-1">
                      <FiEdit size={20} />
                    </div>
                    <h3 className="text-base font-black text-slate-900 tracking-tight">Name Your Template</h3>
                    <p className="text-[9px] text-slate-500 font-bold uppercase tracking-wider">Finalize how this checklist appears in your library.</p>
                  </div>

                  <div className="space-y-4 bg-slate-50 border border-slate-100 p-6 rounded-2xl">
                    <div className="space-y-1.5">
                      <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.15em] pl-1">Template Name</label>
                      <input
                        type="text"
                        value={customName}
                        onChange={(e) => setCustomName(e.target.value)}
                        placeholder={suggestedDetails.name}
                        className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-900 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all placeholder:text-slate-300"
                      />
                    </div>

                    <div className="space-y-1.5 pt-2">
                      <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.15em] pl-1">Description</label>
                      <textarea
                        value={customDesc}
                        onChange={(e) => setCustomDesc(e.target.value)}
                        rows={2}
                        placeholder={suggestedDetails.description}
                        className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2 text-xs font-semibold text-slate-600 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all resize-none placeholder:text-slate-300"
                      />
                    </div>
                  </div>
                </div>
              ) : null
            )}
          </div>

          <div className="px-8 py-5 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
            <button
              onClick={() => {
                if (sourceMode === 'SMART' && wizardStep > 1) handleWizardBack();
                else if (step > 1) { setStep(step - 1); }
                else onClose();
              }}
              className="px-4 py-2 text-[10px] font-bold text-slate-400 hover:text-slate-900 transition-colors uppercase tracking-wider flex items-center gap-2"
            >
              <FiChevronLeft size={16} /> {wizardStep > 1 || step > 1 ? 'Back' : 'Cancel'}
            </button>

            <button
              disabled={isGenerating || (sourceMode === 'SYNC' && !selectedBlueprintId) || (sourceMode === 'SMART' && wizardStep === 8 && !selectedFramework)}
              onClick={() => {
                if (sourceMode === 'SYNC') handleGenerate();
                else if (wizardStep === 10) {
                  handleGenerate();
                } else handleWizardNext();
              }}
              className={`px-8 py-3 rounded-xl font-bold text-[11px] shadow-lg transition-all ${
                isGenerating ? 'bg-slate-400 text-white animate-pulse' : 'bg-[#0A1A44] text-[#58CC02] hover:shadow-green-500/20 active:scale-95'
              } disabled:opacity-30 disabled:pointer-events-none uppercase tracking-widest`}
            >
              {isGenerating ? 'Processing...' : (sourceMode === 'SYNC' ? 'Sync Blueprint' : (wizardStep === 10 ? 'Build Checklist' : 'Next Step'))}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
